import type { RefObject } from 'react';

import type { NavigationBarInternalState } from '@hh.ru/magritte-ui-navigation-bar/types';

/**
 * Фцнкция вычисляющая на основе scrollTop и сотояния NavigationBar значение scrollGroundPos - "нулевой" отметки
 * скролла, при прохождении которой начнет проигрываться анимация изменения внешнего вида NavigationBar
 * Алгоритм вычисления может быть разными и зависит от пропса transformTriggerPosition
 *
 * @param state Объект храняший внутреннее состояние NavigationBar
 * @param newScrollTop Значение скролла
 * @param scrollContainerRef Ссылка на элемент, в котором лежит NavigationBar и контент, и от скролла которого зависит
 * прогресс анимации NavigationBar
 * @param wrapperRef Элемент-обертка вокруг NavigationBar, который не меняет размер или положение в зависимости от
 * прогресса анимации
 * @returns void
 */
export const updateScrollGroundPos = (
    state: NavigationBarInternalState,
    newScrollTop: number,
    scrollContainerRef: RefObject<HTMLElement>,
    wrapperRef: RefObject<HTMLElement>
): void => {
    const { sizes, allowOptionsScroll } = state;
    // высота которая будет после анимации
    const targetSize = sizes.standardTitle + (allowOptionsScroll ? 0 : sizes.optionsContainer);
    let maxOffset = 0;
    const prevScrollTop = state.currentScrollTop;

    // на мобильных устройствах скролл может быть отрицательным из-за overscroll
    const scrollTop = Math.max(newScrollTop, 0);
    state.currentScrollTop = scrollTop;

    if (state.size === 'large') {
        // высота до анимации - высота после анимации = максимальный сдвиг
        maxOffset = sizes.itemsContainer + sizes.largeTitle + sizes.optionsContainer - targetSize;
    } else if (state.size === 'standard') {
        // 40 потому что для заголовка standard всегда есть анимация минимум на 40 пикселей
        maxOffset = Math.max(sizes.standardTitle + sizes.optionsContainer - targetSize, 40);
    }

    if (state.containerObserver && typeof state.transformTriggerPosition === 'string') {
        state.containerObserver.disconnect();
        state.containerObserver = null;
    }

    // триггер в начале скролла
    if (state.transformTriggerPosition === 'start') {
        state.scrollGroundPos = 0;
        return;
    }

    // триггер по все площади контента
    if (state.transformTriggerPosition === 'full-area') {
        // дополнительно проверяем в первом условии transitionProgress, т.к. при отключении анимации у всех
        // элементов animationProgress будет равен Number.POSITIVE_INFINITY, при этом при скролле вверх нам
        // нужно чтобы сработало первое условие, иначе при включении анимации scrollGroundPos будет смещен на -maxOffset
        // от нужного значения
        if (scrollTop < prevScrollTop && (state.animationProgress <= 0 || state.transitionProgress === 0)) {
            state.scrollGroundPos = scrollTop;
        } else if (scrollTop > prevScrollTop && state.animationProgress >= 1) {
            state.scrollGroundPos = scrollTop - maxOffset;
        }

        // при изменении пропса transformTriggerPosition значение может выйти за допустимые ограничения,
        // возвращаем его в допустимый интервал
        state.scrollGroundPos = Math.min(Math.max(state.scrollGroundPos, scrollTop - maxOffset), scrollTop);
        return;
    }

    // триггер - элемент контента
    if (!state.containerObserver && scrollContainerRef.current) {
        const handler = () => {
            if (!wrapperRef.current) {
                return;
            }
            const elementRef = state.transformTriggerPosition as RefObject<HTMLElement>;
            let elementTop = elementRef.current?.getBoundingClientRect?.().top ?? null;
            if (elementTop === null) {
                state.scrollGroundPos = 0;
                return;
            }

            elementTop += state.currentScrollTop;

            // положение обертки не должно зависеть от currentScrollTop, поэтому этот параметр не
            // используем в вычислении
            const navigationBarBottom = wrapperRef.current.getBoundingClientRect().bottom;

            state.scrollGroundPos = elementTop < navigationBarBottom ? 0 : elementTop - navigationBarBottom;
        };
        state.containerObserver = new ResizeObserver(handler);
        state.containerObserver.observe(scrollContainerRef.current);

        // первый раз вызываем синхронно, чтобы при смене пропсов компонент отрисовался правильно
        // иначе handler будет вызван асинхронно, а значит на момент вызова applyAnimations значение
        // state.scrollGroundPos будет равно нулю и придет в норму только при следующем скролле
        handler();
    }
};
